<template>
  <v-footer
    id="footer"
    class="
      footer
      body-2
      pt-xs-6
      sn-caption sn-text-primary-01
      d-flex
      align-start
    "
    data-cy="footer"
    :style="footerStyle"
  >
    <v-container
      :class="`${ (showLongFooter && !mobileView) ? 'long-footer-border-top' : '' }`"
      data-cy="long-footer"
    >
      <v-row
        v-if="mobileView && splashHeaderAllowLocale"
        class="mb-4 d-flex justify-center"
      >
        <h3 class="subheading">
          <span @click="switchLocale('en')">
            English
          </span>
          |
          <span @click="switchLocale('es')">
            Español
          </span>
        </h3>
      </v-row>
      <v-row
        v-if="showMobileContent && twiloEnabled"
        class="app-store-icons mb-5 d-flex"
        :class="(!showLongFooter || mobileView) ? 'justify-center' : 'row-margin'"
      >
        <img
          v-if="userAgent === 'Android' && $store.inMobileApp"
          alt="Click to install app from Google Play store"
          :src="googlePlay"
          class="hoverable"
          tabindex="0"
          @click="goToInstallLink()"
        >
        <img
          v-else-if="userAgent !== 'iOS' && !$store.inMobileApp"
          alt="Click to install app from Google Play store"
          data-cy="google-play-store-footer-button"
          :src="googlePlay"
          class="hoverable"
          tabindex="0"
          @click="mobileAppModal = true; mobileAppType = 'Google Play'"
          @keyup.enter="mobileAppModal = true; mobileAppType = 'Google Play'"
        >
        <img
          v-if="userAgent === 'iOS' && $store.inMobileApp"
          alt="Click to install app from Apple app store"
          :src="appleStore"
          class="hoverable"
          tabindex="0"
          @click="goToInstallLink()"
        >
        <img
          v-else-if="userAgent !== 'Android' && !$store.inMobileApp"
          alt="Click to install app from Apple app store"
          data-cy="apple-store-footer-button"
          :src="appleStore"
          tabindex="0"
          class="hoverable"
          @click="mobileAppModal = true; mobileAppType = 'Apple app'"
          @keyup.enter="mobileAppModal = true; mobileAppType = 'Apple app'"
        >
      </v-row>

      <v-row
        v-if="!hideEqualHousingLenderLogo"
        class="mb-5 d-flex logo-row"
        data-cy="logo-row"
        :class="(!showLongFooter || mobileView) ? 'justify-center' : 'row-margin'"
      >
        <template v-for="(logo, index) in logos">
          <div :key="index">
            <img
              :src="`${logo.iconUrl}`"
              :alt="`${logo.iconName} logo`"
              :data-cy="`${logo.iconName}-logo`"
              height="30"
              class="mx-3 my-1"
            >
            <span>{{ logoMap[logo.iconName] }}</span>
            <span v-if="index !== logos.length - 1">
              &nbsp;|&nbsp;
            </span>
          </div>
        </template>
      </v-row>

      <v-row
        class="mb-5 d-flex"
        data-cy="contact-info-row"
        :class="(!showLongFooter || mobileView) ? 'justify-center' : 'row-margin'"
      >
        <span
          v-if="!hideServicerInfo && fullName"
          data-cy="contact-servicer"
        >
          Questions? Contact {{ fullName }}
        </span>
        <span
          v-if="!hideServicerInfo && servicerTitle"
          data-cy="servicer-title"
        >
          &nbsp;| {{ servicerTitle }}
        </span>
        <span
          v-if="!hideServicerInfo && emailAddress"
          data-cy="servicer-email"
        >
          <a
            :href="`mailto:${emailAddress}`"
            class="link-text no-underline"
            tabindex="0"
          >
            &nbsp;| {{ emailAddress }}
          </a>
        </span>
        <span
          v-if="!hideServicerInfo && cellPhone"
          data-cy="cell-phone"
        >&nbsp;| Cell: {{ cellPhone }} </span>
        <span
          v-if="!hideServicerInfo && workPhone"
          data-cy="work-phone"
        >&nbsp;| Work: {{ workPhone }} </span>
      </v-row>

      <v-row
        class="mb-5 d-flex"
        data-cy="licenses-row"
        :class="(!showLongFooter || mobileView) ? 'justify-center' : 'row-margin'"
      >
        <span
          v-if="!hideServicerInfo && companyAddress"
          data-cy="company-address"
        >
          {{ companyAddress }}
          |&nbsp;
        </span>
        <span data-cy="company-name">
          {{ companyName }}
          <span
            v-if="!hideNMLS"
            data-cy="company-NMLS-number"
          >
            {{ companyNMLS }}
          </span>
        </span>
        <span
          v-if="(fullName || formattedNMLS) && !hideNMLS"
          data-cy="servicer-nmls"
        >
          &nbsp;|
          {{ fullName }}
          {{ formattedNMLS }}
        </span>
        <span v-if="(stateLicenses && stateLicenses.length) && (!showLongFooter || mobileView)">
          &nbsp;|
          <a
            class="link-text underline"
            data-cy="licensing-link"
            tabindex="0"
            @click.stop="openModal('licenses')"
          >
            Licensing
          </a>
        </span>
        <span
          v-if="(stateLicenses && stateLicenses.length) && (showLongFooter && !mobileView)"
          style="width: 100%; margin-top: 10px;"
          data-cy="static-state-licenses"
        >
          <p>
            <span
              v-for="(license, index) in stateLicenses"
              :key="index"
              :data-cy="license.state + '-license'"
            >
              {{ StatesMixin_getNameByAbbr(license.state) }}: {{ license.value }} <span v-if="index !== stateLicenses.length - 1">|</span>
            </span>
          </p>
        </span>
        <span v-if="disclaimer && (!showLongFooter || mobileView)">
          &nbsp;|
          <a
            class="link-text underline"
            data-cy="disclaimer-link"
            tabindex="0"
            @click.stop="openModal('disclaimer')"
          >
            Disclaimer
          </a>
        </span>
        <span
          v-if="disclaimer && (showLongFooter && !mobileView)"
          class="mb-4"
          style="width: 100%;"
          v-html="disclaimer"
        />
        <span v-if="termsOfUse">
          <span v-if="!showLongFooter || mobileView">
            &nbsp;|
          </span>
          <a
            :href="termsOfUse"
            target="_blank"
            rel="noopener nofollow"
            aria-label="link to terms of use"
            class="link-text underline"
            tabindex="0"
            data-cy="terms-of-use-link"
          >
            Terms & Conditions
          </a>
        </span>
        <span v-if="privacyPolicy">
          <span v-if="termsOfUse || !showLongFooter || mobileView">
            &nbsp;|
          </span>
          <a
            :href="privacyPolicy"
            target="_blank"
            rel="noopener nofollow"
            aria-label="link to privacy policy"
            class="link-text underline"
            tabindex="0"
            data-cy="privacy-policy-link"
          >
            Privacy Policy
          </a>
        </span>
        <span v-if="nmlsConsumerAccessLink && !hideNMLS">
          <span v-if="termsOfUse || privacyPolicy || !showLongFooter || mobileView">
            &nbsp;|
          </span>
          <a
            :href="nmlsConsumerAccessLink"
            target="_blank"
            rel="noopener nofollow"
            aria-label="link to nmls consumer access link"
            class="link-text underline"
            tabindex="0"
            data-cy="nmls-link"
          >
            NMLS
          </a>
        </span>
        <span v-if="shouldShowCustomLink">
          <span v-if="termsOfUse || privacyPolicy || nmlsConsumerAccessLink || !showLongFooter || mobileView">
            &nbsp;|
          </span>
          <a
            :href="customLinkUrl"
            target="_blank"
            rel="noopener nofollow"
            aria-label="link to terms of use"
            class="link-text underline"
            tabindex="0"
            :data-cy="customLinkText + '-link'"
          >
            {{ customLinkText }}
          </a>
        </span>
      </v-row>

      <template v-if="ncinoPrivacyPolicyEnabled && dataSaleEnabled">
        <v-row
          v-if="user"
          class="mb-5 d-flex"
          data-cy="privacy-policy-row"
          :class="(!showLongFooter || mobileView) ? 'justify-center' : 'row-margin'"
        >
          <a
            class="link-text underline"
            tabindex="0"
            data-cy="privacy-policy-button"
            @click.stop="togglePrivacyPolicyDialog"
          >
            {{ privacyPolicyText }}
          </a>
          <span>
            <span>&nbsp;|</span>
            <a
              class="link-text underline"
              tabindex="0"
              data-cy="do-not-sell-button"
              @click.stop="showPrivacySettings"
            >
              {{ doNotSellMyInfoText }}
            </a>
          </span>
        </v-row>
      </template>

      <v-row
        class="mb-5 d-flex"
        data-cy="partnering-row"
        :class="(!showLongFooter || mobileView) ? 'justify-center' : 'row-margin'"
      >
        <a
          v-if="usingNcinoLogo"
          href="https://www.ncino.com/"
          target="_blank"
          style="text-decoration: none"
        >
          <span style="color: grey">Powered by </span>
          <img
            alt="Powered by nCino"
            src="~/assets/images/ncino-watermark.png"
            height="27"
            style="margin-bottom: -6px; margin-left: 4px"
          >
        </a>
        <a
          v-else
          href="https://www.ncino.com/"
          target="_blank"
          tabindex="0"
        >
          <img
            alt="Powered by nCino"
            data-cy="sn-partnering"
            src="/assets/ncino-watermark.svg"
          >
        </a>
      </v-row>
      <sn-modal
        v-model="modal"
        :title="title"
        data-cy="footer-modal"
        type="dismiss"
      >
        <span
          v-if="isDisclaimer"
          data-cy="disclaimer-text"
          v-html="disclaimer"
        />
        <v-row
          v-for="(license, index) in stateLicenses"
          v-else
          :key="index"
          class="d-flex justify-center license-row"
          data-cy="license-row"
        >
          <span class="license-state">
            <strong data-cy="state">
              {{ StatesMixin_getNameByAbbr(license.state) }}
            </strong>
          </span>
          <span
            class="license-value"
            data-cy="state-license-value"
          >
            {{ license.value }}
          </span>
        </v-row>
      </sn-modal>
      <sn-modal
        v-model="mobileAppModal"
        title="Text me the app"
        primary-text="Text me the app"
        type="decision"
        data-cy="mobile-app-modal"
        :disabled="!canSubmit"
        @submit="sendSMS"
      >
        <p>
          Add your mobile phone number below and we'll text you a
          direct link to the {{ companyName }} app in the
          {{ mobileAppType }} store.
        </p>
        <span>
          Mobile phone number
        </span>
        <sn-text-field
          v-model="phone"
          v-mask="'(###) ###-####'"
          type="tel"
          :placeholder="'(___) ___-____'"
          :rules="rules.phone"
          :validate-on-blur="true"
        />
      </sn-modal>
      <sn-toast
        v-model="toast.active"
        :event="toast.event"
        :message="toast.message"
        :description="toast.description"
      />
    </v-container>
  </v-footer>
</template>

<script>
import { NullServicer } from '../Servicer/nullProfile'
import { formatNMLS } from '../../Shared/formatNMLS.js'
import googlePlay from '~/assets/images/google_play_sm-eng.png'
import appleStore from '~/assets/images/app_store_sm-eng.png'
import statesMixin from '../../../common/mixins/StatesMixin'
import snValidationsService from '~/services/snValidationsService.js'
import snUtilService from '~/services/snUtilService.js'
import localeMixin from '../../Onboarding/Shared/localeMixin.js'
import { mapActions } from 'vuex'

export default {
  name: 'FooterWrapper',
  mixins: [statesMixin, localeMixin],
  props: {
    servicer: {
      type: Object,
      required: false,
      default: () => {
        return new NullServicer()
      }
    },
    sac: {
      type: String,
      required: false,
      default: ''
    },
    ncinoPrivacyPolicyEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    dataSaleEnabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      companyDefaultCode: '',
      showChooseLo: false,
      store: this.$store,
      chooseLoTitleText: '',
      googlePlay: googlePlay,
      appleStore: appleStore,
      title: '',
      isDisclaimer: false,
      modal: false,
      mobileAppModal: false,
      mobileAppType: '',
      phone: '',
      doNotSellMyInfoText: 'Do not sell my personal information',
      privacyPolicyText: 'nCino Mortgage Privacy & Use',
      rules: {
        phone: [
          (v) => !!v || 'Required',
          (v) => snValidationsService.phone(v) || 'Please enter a valid phone number.'
        ]
      },
      toast: {
        active: false,
        event: null,
        message: '',
        description: ''
      }
    }
  },
  computed: {
    servicerProfile () {
      return this.servicer?.effective_company_nmls !== 'Loading' || !this.$store
        ? this.servicer : this.$store.servicer
    },
    activationCode () {
      return this.sac || this.$store.activationCode || this.$store.defaultCode
    },
    companyName () {
      return this.servicerProfile?.effective_company_name
    },
    companyNMLS () {
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        return this.store.footerInfo.effective_company_nmls
      } else {
        return this.servicerProfile?.effective_company_nmls
      }
    },
    fullName () {
      return this.servicerProfile?.full_name
    },
    formattedNMLS () {
      return formatNMLS(this.servicerProfile?.license)
    },
    emailAddress () {
      return this.servicerProfile?.email || this.$route.params.code
    },
    supportEmail () {
      return this.$store.support.email
    },
    logoMap () {
      return {
        EHL: 'Equal Housing Lender',
        EHO: 'Equal Housing Opportunity',
        EOL: 'Equal Opportunity Lender',
        HUD: 'HUD',
        MBA: 'Member MBA',
        FDIC: 'Member FDIC',
        NCUA: 'NCUA'
      }
    },
    logos () {
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        return this.store.footerInfo.effectiveEhLogos
      } else {
        return this.servicerProfile?.effectiveEhLogos
      }
    },
    usingNcinoLogo () {
      return this.$store.borrowerWebContent?.useNcinoLogo
    },
    cellPhone () {
      if (this.hideCell) return false
      const phone = this.servicerProfile?.unformatted_phone
      return (phone && phone.length === 10)
        ? phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        : undefined
    },
    workPhone () {
      if (this.hideWork) return false
      return this.servicerProfile?.office_phone || undefined
    },
    privacyPolicy () {
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        return this.store.footerInfo.effective_privacy_policy_link
      } else {
        return this.servicerProfile?.effective_privacy_policy_link || undefined
      }
    },
    termsOfUse () {
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        return this.store.footerInfo.effective_terms_of_use_link
      } else {
        return this.servicerProfile?.effective_terms_of_use_link || undefined
      }
    },
    nmlsConsumerAccessLink () {
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        return this.store.footerInfo.consumer_access_link
      } else {
        return this.servicerProfile?.consumer_access_link || undefined
      }
    },
    servicerTitle () {
      return this.servicerProfile?.title
    },
    stateLicenses () {
      let raw
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        raw = this.store.footerInfo.state_lics?.map(item => ({
          state: item.state_license?.state_code || '',
          value: item.state_license_val || ''
        }))
      } else if (this.servicerProfile) {
        raw = this.servicerProfile.state_lics
      }

      return raw instanceof Array ? raw : (
        raw !== undefined ? JSON.parse(raw) : []
      )
    },
    disclaimer () {
      if (this.store?.genericOrganizationFooter && this.store?.footerInfo) {
        return this.store.footerInfo.effective_website_disclaimer
      } else {
        return this.servicerProfile?.effective_website_disclaimer || undefined
      }
    },
    userAgent: function () {
      var userAgent = navigator.userAgent || navigator.vendor || window.opera

      if (/windows phone/i.test(userAgent)) {
        return 'Windows Phone'
      } else if (/android/i.test(userAgent)) {
        return 'Android'
      } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'iOS'
      }

      return 'unknown'
    },
    getColorTheme () {
      return this.$vuetify.theme.themes.light.primary
    },
    footerStyle () {
      const hexColor = this.getColorTheme
      const rgbColor = this.hexToRgbA(hexColor, '0.5')
      const height = '5px'
      return '--background-color: ' + hexColor + '; --gradient-color: ' + rgbColor + '; --header-height: ' + height + ';'
    },
    hideEqualHousingLenderLogo () {
      return this.$store?.borrowerWebContent?.hideEqualHousingFooterLogo
    },
    hideNMLS () {
      return this.$store?.borrowerWebContent?.hideNmlsInFooter
    },
    hideServicerInfo () {
      return this.$store?.borrowerWebContent?.hideFooterServicerInfo
    },
    hideCell () {
      return this.store?.borrowerWebContent?.splashFooterCell === ''
    },
    hideWork () {
      return this.store?.borrowerWebContent?.splashFooterWorkPhone === ''
    },
    shouldShowCustomLink () {
      return this.store?.borrowerWebContent?.footerCustomLinkEnabled ?? false
    },
    customLinkUrl () {
      return this.store?.borrowerWebContent?.footerCustomLinkUrl ?? ''
    },
    customLinkText () {
      return this.store?.borrowerWebContent?.footerCustomLinkText ?? ''
    },
    companyAddress () {
      if (this.store?.borrowerWebContent?.footerAddressEnabled && this.servicerProfile?.street1 && this.servicerProfile?.city && this.servicerProfile?.state && this.servicerProfile?.zip) {
        return `${this.servicerProfile.street1} ${this.servicerProfile.street2} ${this.servicerProfile.city}, ${this.servicerProfile.state} ${this.servicerProfile.zip}`
      } else return ''
    },
    mobileView () {
      return this.$vuetify.breakpoint.smAndDown || (this.store && this.store.inMobileApp)
    },
    showLongFooter () {
      return this.servicerProfile?.feature_setting_show_long_footer
    },
    splashHeaderAllowLocale () {
      return this.$store.borrowerWebContent?.splashHeaderAllowLocale
    },
    canSubmit () {
      return snValidationsService.phone(this.phone)
    },
    showMobileContent () {
      return this.store?.borrowerWebContent?.showMobileContentOnLandingPage ?? true
    },
    twiloEnabled () {
      return this.store?.servicer?.parentOrganization?.organization_setting_twilio_sms_enabled ?? false
    },
    user () {
      return this.store?.currentUser
    },
    isBorrowerSignedIn () {
      return Boolean(this.$store?.currentUser?.app_user?.id)
    }
  },
  methods: {
    ...mapActions('privacyPolicyStore', ['togglePrivacyPolicyDialog']),
    goToInstallLink () {
      window.location.replace(`/install/app/${this.activationCode}?skip_mobile_signup=1`)
    },
    openModal (modal) {
      switch (modal) {
        case 'licenses':
          this.title = 'License Info'
          this.isDisclaimer = false
          break

        case 'disclaimer':
          this.title = 'Disclaimer'
          this.isDisclaimer = true
          break
      }

      this.modal = !this.modal
    },
    showPrivacySettings () {
      this.$emit('showPrivacySettings')
    },
    hexToRgbA (hex, alpha) {
      if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        let c = hex.substring(1).split('')
        if (c.length === 3) {
          c = [c[0], c[0], c[1], c[1], c[2], c[2]]
        }
        c = '0x' + c.join('')
        return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + alpha + ')'
      }
      throw new Error('Bad Hex')
    },
    sendSMS () {
      if (!this.phone) return

      const phone = this.phone.replace(/\D/g, '')
      const code = this.activationCode
      const source = this.isBorrowerSignedIn ? 'borrower_share' : 'landing_page'

      snUtilService
        .shareAppBySms(phone, code, 'borrower_invitation', source)
        .then(() => {
          this.toast.active = true
          this.toast.event = 'success'
          this.toast.message = 'Message sent successfully'
          this.mobileAppModal = false
          this.phone = ''
        })
        .catch(() => {
          this.toast.active = true
          this.toast.event = 'error'
          this.toast.message = 'Failed to send message'
        })
    }
  }
}
</script>

<style scoped lang="scss">
@import "~/assets/css/snui";

@media screen and (max-width: $medium-screen-size) {
  .footer::v-deep {
    background-color: $sn-gray-01 !important;
  }
}

@media screen and (min-width: ($medium-screen-size + 1)) {
  .footer::v-deep {
    background-color: $sn-white !important;
  }
}

.footer::v-deep {
  background-color: $sn-white !important;
  padding: 6px 0px;
  width: 100%;
  z-index: 5;

  &:after {
    content:"";
    background: linear-gradient(270deg, var(--background-color) 0%, var(--gradient-color) 50%, var(--background-color) 100%);
    display: block;
    height:5px;
    width: 100%;
    position: absolute;
    bottom: 0;
  }

  .container {
    padding-bottom: 0px;
  }
}
.app-store-icons img {
  margin-left: 8px;
  margin-right: 8px;
  width: 135px;
}
.last-row {
  margin-bottom: -10px;
}
.link-text {
  color: $sn-primary-01;
}
.no-underline {
  text-decoration: none;
}
.underline {
  text-decoration: underline;
}
.hoverable {
  cursor: pointer;
}
.EHL-logo {
  height: 14px;
  margin-right: 5px;
  align-self: center;
}
.long-footer-border-top {
  padding-top: 0px;

  .row:first-child {
    padding-top: 12px;
    border-top: 1px solid $sn-gray-04;
  }

  .logo-row {
    div:first-child img {
      margin-left: 0px !important;
    }
  }
}
.v-application {
  .mb-5 {
    margin-bottom: 10px !important;

    &.logo-row {
      align-items: center;

      img {
        vertical-align: middle;
      }
    }

    &.row-margin {
      margin-left: 15px;
      margin-right: 15px;
    }
  }
}
.license-row {
  border-bottom: 1px solid $sn-gray-04;
  width: 100%;
  padding: 10px 0px;
  margin: 0;

  &:last-child {
    border-bottom: none;
  }
}
.license-state {
  width: 30%;
}
.license-value {
  width: 70%;
}

@media (max-width: 979px) {
  .license-state {
    width: 40%;
  }
  .license-value {
    width: 60%;
  }
}

@media (max-width: 539px) {
  .footer::v-deep {
    margin-top: 0px !important;
  }
}
</style>
